import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ShieldAlert, Loader2, Sparkles, AlertTriangle, Clock, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import logger from "@/lib/logger";

const ENTITY_LINKS = {
  capa: (id) => `/capa/${id}`,
  deviation: () => `/deviations`,
  change: () => `/changes`,
  document: () => `/documents`,
  training_assignment: () => `/training`,
  risk: () => `/risk`,
};

const SEV_COLORS = {
  Critical: "bg-red-100 text-red-800 border-red-300",
  High: "bg-amber-100 text-amber-800 border-amber-300",
  Medium: "bg-yellow-100 text-yellow-800 border-yellow-300",
  Low: "bg-emerald-100 text-emerald-800 border-emerald-300",
};

export default function GapDetector() {
  const [scan, setScan] = useState(null);
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const [s, h] = await Promise.all([
        api.get("/gap-detector/latest"),
        api.get("/gap-detector/history"),
      ]);
      setScan(s.data.scan_id ? s.data : null);
      setHistory(h.data);
    } catch (e) {
      // First load may 404 if no scan has been run yet — only log unexpected errors.
      if (e?.response && e.response.status !== 404) {
        logger.warn("GapDetector load failed:", e.response.status);
      }
    }
  };
  useEffect(() => { load(); }, []);

  const runScan = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/gap-detector/scan");
      setScan(data);
      toast.success(`Scan complete — ${data.gap_count} gap(s) found`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed");
    } finally {
      setRunning(false);
    }
  };

  const filteredGaps = (scan?.gaps || []).filter((g) => filter === "all" || g.severity === filter);

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="vq-overline mb-2">Inspection-Prevention</div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">Compliance Gap Detector</h1>
            <p className="text-slate-600 mt-2 max-w-2xl">
              Deterministic scan across every audit event and e-signature to surface anomalies before an inspector does.
              AI-assisted findings must be reviewed by qualified personnel.
            </p>
          </div>
          <Button onClick={runScan} disabled={running} data-testid="gap-scan-btn"
                  className="h-11 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
            {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning…</> : <><RefreshCw className="w-4 h-4 mr-2" /> Run scan</>}
          </Button>
        </div>

        {!scan ? (
          <div className="vq-card p-10 text-center">
            <ShieldAlert className="w-10 h-10 text-slate-400 mx-auto mb-4" />
            <h3 className="font-display font-bold text-xl text-slate-900 mb-2">No scans yet</h3>
            <p className="text-slate-600 max-w-md mx-auto mb-6">
              Run your first scan to check for missing signatures, stalled CAPAs, overdue training, and other compliance anomalies.
            </p>
            <Button onClick={runScan} disabled={running} className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
              Run first scan
            </Button>
          </div>
        ) : (
          <>
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              <div className="vq-card p-5 md:col-span-1">
                <div className="vq-overline mb-1">Total gaps</div>
                <div className="font-display font-extrabold text-3xl text-slate-900" data-testid="gap-total">{scan.gap_count}</div>
              </div>
              {["Critical", "High", "Medium", "Low"].map((sev) => (
                <div key={sev} className="vq-card p-5" data-testid={`gap-count-${sev}`}>
                  <div className="vq-overline mb-1">{sev}</div>
                  <div className={`font-display font-extrabold text-3xl ${scan.by_severity[sev] > 0 && sev !== "Low" ? "text-red-600" : "text-slate-900"}`}>
                    {scan.by_severity[sev] || 0}
                  </div>
                </div>
              ))}
            </div>

            {/* AI narrative */}
            {scan.ai_summary && (
              <div className="vq-card p-6 mb-6 border-l-4 border-l-[#0055FF]">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-[#0055FF]" />
                  <div className="vq-overline">AI-assisted executive summary</div>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed" data-testid="gap-ai-summary">{scan.ai_summary}</p>
                <p className="text-[10px] font-mono text-slate-400 mt-3">
                  ⚠ AI-generated summary. Findings below are deterministic — review with qualified personnel.
                </p>
              </div>
            )}

            {/* Filters */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="text-xs font-mono uppercase tracking-wider text-slate-500 mr-2">Filter:</span>
              {["all", "Critical", "High", "Medium", "Low"].map((f) => (
                <button key={f} onClick={() => setFilter(f)} data-testid={`gap-filter-${f}`}
                        className={`px-3 py-1 rounded-md text-xs font-semibold border transition-colors ${
                          filter === f ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-600 hover:border-slate-900"
                        }`}>
                  {f} {f !== "all" && `(${scan.by_severity[f] || 0})`}
                </button>
              ))}
              <span className="ml-auto text-[10px] font-mono text-slate-500">
                <Clock className="w-3 h-3 inline mr-1" />Scanned {new Date(scan.run_at).toLocaleString()} by {scan.run_by}
              </span>
            </div>

            {/* Gaps list */}
            <div className="space-y-3">
              {filteredGaps.length === 0 ? (
                <div className="vq-card p-10 text-center">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ShieldAlert className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-slate-900 mb-1">All clear at this severity</h3>
                  <p className="text-sm text-slate-500">Try another filter or run a fresh scan.</p>
                </div>
              ) : filteredGaps.map((g) => {
                const linkFn = ENTITY_LINKS[g.entity_type];
                const to = linkFn ? linkFn(g.entity_id) : null;
                return (
                  <div key={g.gap_id} className="vq-card p-5" data-testid={`gap-item-${g.gap_id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${SEV_COLORS[g.severity]}`}>
                            {g.severity}
                          </span>
                          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{g.rule}</span>
                          <span className="text-[10px] font-mono text-slate-400">•</span>
                          <span className="text-[10px] font-mono text-slate-400">{g.iso_ref}</span>
                        </div>
                        <div className="font-display font-bold text-slate-900 mb-1">{g.finding}</div>
                        <div className="text-sm text-slate-600">
                          <span className="text-slate-500 font-mono uppercase tracking-wider text-[10px] mr-2">Entity:</span>
                          <span className="font-mono text-xs">{g.entity_type}/{g.entity_id}</span>
                          <span className="ml-2 text-slate-700">— {g.entity_title}</span>
                        </div>
                        <div className="text-sm text-slate-700 mt-2 p-3 bg-slate-50 border border-slate-200 rounded">
                          <span className="text-slate-500 font-mono uppercase tracking-wider text-[10px] mr-2">Recommendation:</span>
                          {g.recommendation}
                        </div>
                      </div>
                      {to && (
                        <Link to={to} data-testid={`gap-open-${g.gap_id}`}>
                          <Button variant="outline" size="sm" className="border-slate-300 font-semibold">
                            Open <ExternalLink className="w-3 h-3 ml-1" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* History */}
            {history.length > 1 && (
              <div className="mt-10">
                <div className="vq-overline mb-3">Scan history</div>
                <div className="vq-card overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left px-4 py-2 text-xs font-mono uppercase tracking-wider text-slate-500">Run at</th>
                        <th className="text-left px-4 py-2 text-xs font-mono uppercase tracking-wider text-slate-500">By</th>
                        <th className="text-right px-4 py-2 text-xs font-mono uppercase tracking-wider text-slate-500">Gaps</th>
                        <th className="text-right px-4 py-2 text-xs font-mono uppercase tracking-wider text-slate-500">Critical</th>
                        <th className="text-right px-4 py-2 text-xs font-mono uppercase tracking-wider text-slate-500">High</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h) => (
                        <tr key={h.scan_id} className="border-b border-slate-100">
                          <td className="px-4 py-2 text-xs font-mono text-slate-600">{new Date(h.run_at).toLocaleString()}</td>
                          <td className="px-4 py-2 text-xs font-mono text-slate-600">{h.run_by}</td>
                          <td className="px-4 py-2 text-sm text-right font-semibold">{h.gap_count}</td>
                          <td className="px-4 py-2 text-sm text-right text-red-600 font-mono">{h.by_severity?.Critical || 0}</td>
                          <td className="px-4 py-2 text-sm text-right text-amber-600 font-mono">{h.by_severity?.High || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
